<template>
  <div class="txl-chat" data-testid="txl-chat-panel">
    <!-- ── head ───────────────────────────────────────────────────────────
         The read-only chip lives INSIDE this panel (not only in the page
         header) because the chat column is the one place a reader instinctively
         looks for a composer. Saying it here answers the question where it is
         actually asked. -->
    <div class="txl-chat-head d-flex align-center flex-wrap ga-2 pb-1">
      <span class="text-subtitle-2">บทสนทนา</span>
      <span v-if="turnCount" class="text-caption text-medium-emphasis">{{ turnCount }} เทิร์น</span>
      <v-spacer />
      <!-- 🔴 KEPT even though it is one more control on a read-only page: this viewer is
           where an admin checks WHAT PRODUCTION ACTUALLY SENT, and "where does the marker
           sit relative to the question after it" is only answerable on the raw string.
           Purely local view state — it flips which copy of the text the bubble prints and
           writes nothing. Default OFF = cards visible; raw wire is the deliberate detour.
           🔴 Only offered when SOME BUBBLE ACTUALLY HAS A RAW WIRE TO SHOW (`hasRawWire`):
           an always-on switch would read as a page-wide mode and invite "why is nothing
           different?" on ordinary turns. It used to key off `hasCards` — which turned into
           exactly that complaint once cards started arriving in their own `taxCards` field:
           a PLAYGROUND turn has cards while its `reply` carries no marker at all (the backend
           strips it from the transcript), so the switch appeared and flipped between two
           IDENTICAL strings. See `hasRawWire` for why a broken payload still gets a switch. -->
      <v-switch
        v-if="hasRawWire"
        v-model="rawWire"
        density="compact"
        hide-details
        color="primary"
        label="ดู marker ดิบ"
        class="txl-raw-switch flex-grow-0"
        data-testid="txl-raw-wire-toggle"
      />
      <v-chip
        size="x-small"
        variant="tonal"
        prepend-icon="mdi-eye-outline"
        title="หน้านี้ดูประวัติได้อย่างเดียว — ส่งข้อความหรือแก้ไขไม่ได้"
        data-testid="txl-chat-readonly-chip"
      >อ่านอย่างเดียว</v-chip>
    </div>

    <!-- ── bubbles ────────────────────────────────────────────────────────
         Started as a copy of `TaxPlaygroundChatPanelV2.vue` and still shares its
         `.chat-*` class names and its a11y `sr-only` speaker label — but the two
         files have SINCE DIVERGED and are no longer line-for-line twins. Do not
         port a change across without reading both.

         Never existed here (a transcript that has already happened cannot have
         them): the composer, the quick-reply chips, the "เริ่มฟอร์มใหม่" banner,
         the in-flight placeholder.

         Tax cards: this viewer USED to show a card turn's `|taxcard|({…})` payload as
         raw in-band text and nothing else — accurate, but unreadable. It now does what
         the playground does: the parent hands down `cardText` / `cards` and a
         `TaxSummaryCardV2` renders under the bubble, with the same "ดู marker ดิบ"
         switch and the same broken-payload alert. 🔴 One difference that is NOT an
         oversight: cards show on EVERY turn that has one, with no `isActive` condition
         anywhere — the parent reads them from the row's own `taxCards` field, which every
         row carries, so unlike the playground there is no turn whose cards have to wait for
         a full reply to be fetched. There is also no `cardsDiffer` chip: cards come from
         that ONE field and the parent no longer re-splits the reply, so nothing can
         disagree (see the parent's `bubbles()`).

         Still diverged: bubbles here key off `turnNo` (not `turnId`), carry `isError`
         instead of `llmFailed`, and own `.chat-bubble--active` for inspector selection.
         Do not port a change across without reading both files. -->
    <!-- 🔴 ชั้นห่อนี้คือ "จอ" ของแชท: `.chat-scroll` เลื่อนอยู่ข้างใน ส่วน date chip ลอยทับอยู่ข้างบน
         chip ต้องอยู่ **นอก** ตัวที่เลื่อน ไม่งั้นมันจะเลื่อนหนีไปกับเนื้อหา (position: sticky ก็ทำได้
         แต่ sticky บอกได้แค่ "ช่วงนี้เริ่มเมื่อไร" ไม่ใช่ "บรรทัดที่กำลังอ่านอยู่คือเวลาอะไร" ซึ่งคือสิ่งที่ขอ) -->
    <div class="chat-view">
      <div ref="scroller" class="chat-scroll" data-testid="txl-chat-bubbles" @scroll.passive="onScroll">
        <v-skeleton-loader v-if="loading" type="paragraph@3" data-testid="txl-chat-skeleton" />

        <div
          v-else-if="!bubbles.length"
          class="text-medium-emphasis text-caption pa-4 text-center"
          data-testid="txl-chat-empty"
        >
          <v-icon size="28" color="grey-lighten-1">mdi-message-off-outline</v-icon>
          <div class="mt-1">ไม่มีบทสนทนาใน session นี้</div>
          <div class="mt-1">(session ที่มาจาก Tax Playground จะเก็บเทิร์นไว้คนละที่กับแชทจริง)</div>
        </div>

        <template v-else>
          <!-- ── หัวคั่น "ช่วงของฟอร์ม" (เส้นแบ่งในสาย ไม่ลอย) ────────────────────
               🔴 1 session มีได้หลายฟอร์ม (เคยเจอ 32 ใบใน session เดียว) แต่ transcript เป็นสายเดียว
               ไม่มีรอยต่อ ⇒ คนอ่านบอกไม่ได้ว่าบับเบิลตรงหน้าเป็นของฟอร์มใบไหน หัวคั่นนี้บอก "ตรงนี้คือ
               จุดที่ฟอร์มเปลี่ยน / ข้ามวัน" ส่วน "ตอนนี้กำลังอ่านของฟอร์มไหน เวลาอะไร" เป็นหน้าที่ของ
               date chip ที่ลอยอยู่ข้างบน (`.chat-float-chip`) — สองอันตอบคนละคำถาม จึงมีทั้งคู่
               🔴 ตัดช่วงเมื่อ **ฟอร์มเปลี่ยน หรือ ข้ามวัน** อย่างใดอย่างหนึ่ง (`buildChatSegments`)
               🔴 ป้ายของแถว production เป็นค่าที่ **อนุมานจากเวลา** — `dbo.Log_Message` ไม่มีคอลัมน์
               FormID ⇒ มี `≈` กำกับ และ tooltip พูดออกมาตรง ๆ ห้ามถอดออกจนกว่าจะมี FormID จริงต่อเทิร์น -->
          <div v-for="seg in segments" :key="seg.key" class="chat-seg">
            <div class="chat-seg-head" :data-testid="'txl-seg-head-' + seg.formID">
              <button
                type="button"
                class="chat-seg-chip"
                :class="{
                  'chat-seg-chip--active': Boolean(seg.formID) && seg.formID === selectedFormId,
                  'chat-seg-chip--none': !seg.formID,
                }"
                :disabled="!seg.formID"
                :title="segTitle(seg)"
                :data-testid="'txl-seg-chip-' + seg.formID"
                @click="onSegHead(seg)"
              >
                <v-icon size="13" class="mr-1">{{ seg.formID ? 'mdi-file-document-outline' : 'mdi-chat-outline' }}</v-icon>
                <span class="font-weight-medium">{{ seg.formTitle }}</span>
                <!-- `≈` = ป้ายฟอร์มของช่วงนี้เดาจากเวลา ไม่ใช่ค่าที่บันทึกไว้ (คำอธิบายเต็มอยู่ใน tooltip) -->
                <span v-if="seg.formInferred" class="chat-seg-approx">≈</span>
                <span class="chat-seg-dot">·</span>
                <span>{{ segWhen(seg) }}</span>
              </button>
            </div>

            <!-- `data-bubble-key` = ที่จับของ `syncCurrentBubble()` — มันไล่หาบับเบิลแรกที่ยังอยู่ในจอ
                 เพื่อเอาเวลา/ฟอร์มของบรรทัดนั้นไปขึ้นบน date chip · อย่าถอดออก -->
            <div
              v-for="b in seg.bubbles"
              :key="b.key"
              class="d-flex mb-2"
              :class="b.side === 'user' ? 'justify-end' : 'justify-start'"
              :data-bubble-key="b.key"
            >
              <div
                class="chat-col d-flex flex-column"
                :class="[b.side === 'user' ? 'align-end' : 'align-start', { 'chat-col--card': b.cards && b.cards.length }]"
              >
                <!-- 🔴 A CARD-ONLY turn (blank lead text + marker — a shape the node supports on purpose,
                     since most cards carry their own title) would otherwise leave an empty grey bubble
                     floating above the card. The bubble is dropped ONLY when there is nothing to say AND
                     a card is there to say it instead; every card-less bubble — user, system, plain bot —
                     keeps rendering as before, empty or not. (Same guard as
                     `TaxPlaygroundChatPanelV2.vue`, written out here rather than imported: importing that
                     file would drag a composer into a read-only page.)
                     🔴 Selecting such a turn still works — and it matters MORE here than in the
                     playground, because `@click="onBubble"` lives on the bubble alone (the card block
                     below is `@click.stop`) and this page's selection drives the whole 4-tab inspector.
                     The way in is the turn list in `views/TaxLogV2Detail.vue`: the horizontal strip
                     (`txl-turn-strip`, rendered when `!mdAndUp`) below md, and the timeline cards
                     (`txl-turn-card-*`, rendered when `mdAndUp && !inspectorFull`) from md up — expand
                     the rail first if the timeline is collapsed. One of the two is always on screen
                     wherever THIS panel is: `inspectorFull` hides the chat column and the xs chat tab
                     needs `isXs`, so there is no breakpoint that shows the transcript with no turn list. -->
                <div
                  v-if="showBubble(b)"
                  class="chat-bubble"
                  :class="bubbleClass(b)"
                  :title="bubbleTitle(b)"
                  :data-testid="'txl-bubble-' + (b.turnNo || 0) + '-' + b.side"
                  @click="onBubble(b)"
                >
                  <span class="sr-only">{{ srLabel(b) }}</span>{{ bubbleText(b) }}
                </div>

                <!-- 🔴 Cards sit OUTSIDE `.chat-bubble` so expanding one cannot select the turn, and
                     `.stop` keeps it that way if the markup is ever nested back in.
                     🔴 `txl-cards--active` is this file's own addition (the playground has no selection
                     outline): on a CARD-ONLY turn the bubble above is gone, so without it a selected turn
                     could have NO outlined element at all — and `scrollActiveIntoView` would find nothing
                     to scroll to. It matches this class too.
                     🔴 `!showBubble(b)` is the whole point of the gate: the class is a STAND-IN for the
                     bubble's outline, not a second outline next to it. Un-gated it also fired on ordinary
                     card turns, which then drew THREE nested primary outlines at once (user bubble + bot
                     bubble + card block) for one selection. Sharing `showBubble` with the `v-if` above is
                     what keeps the two from drifting — including under "ดู marker ดิบ", where a PRODUCTION
                     card-only turn gets its bubble back (its raw text is the marker itself, so non-empty)
                     and the outline moves with it. A PLAYGROUND card-only turn has no raw wire to show —
                     the marker was stripped server-side, so the switch is not even offered (`hasRawWire`)
                     — and it simply stays outlined by this block. -->
                <div
                  v-if="b.cards && b.cards.length"
                  class="txl-cards mt-2"
                  :class="{ 'txl-cards--active': !showBubble(b) && b.turnKey && b.turnKey === activeTurnKey }"
                  :data-testid="'txl-bubble-cards-' + (b.turnNo || 0)"
                  @click.stop
                >
                  <!-- 🔴 One slot testid PER CARD. A turn that skips two SUMMARY questions sends the
                       marker twice (TaxCardMarkerV2.cs), so two cards in one bubble is normal traffic —
                       without the per-card scope every testid inside the card repeats and can no longer
                       name which of the two it belongs to. -->
                  <div
                    v-for="(c, ci) in b.cards"
                    :key="ci"
                    class="mb-2"
                    :data-testid="'txl-card-slot-' + (b.turnNo || 0) + '-' + ci"
                  >
                    <!-- A card whose JSON did not parse is still EVIDENCE — show the raw text, never drop
                         it. On THIS page that is the point: a production turn whose payload is malformed is
                         exactly what an admin opens the log to find.
                         🔴 `card` is a required prop, so a null `doc` must never reach it. -->
                    <v-alert
                      v-if="!c.doc"
                      type="error"
                      variant="tonal"
                      density="compact"
                      :data-testid="'txl-card-broken-' + (b.turnNo || 0) + '-' + ci"
                    >
                      <div class="text-caption font-weight-medium">อ่าน payload ของการ์ดใบนี้ไม่ได้ (JSON ไม่ถูกต้อง)</div>
                      <pre class="txl-card-raw">{{ c.raw }}</pre>
                    </v-alert>
                    <TaxSummaryCardV2 v-else :card="c.doc" :raw="c.raw" />
                  </div>
                </div>

                <div class="d-flex align-center ga-1 mt-1">
                  <span v-if="b.turnNo" class="chat-time text-caption text-medium-emphasis">#{{ b.turnNo }}</span>
                  <span v-if="b.time" class="chat-time text-caption text-medium-emphasis">{{ fmtTime(b.time) }}</span>
                  <v-chip
                    v-if="b.isError"
                    size="x-small"
                    color="error"
                    variant="tonal"
                    prepend-icon="mdi-alert-circle-outline"
                    title="เทิร์นนี้จบด้วยข้อผิดพลาด — ดูรายละเอียดในแท็บ Trace"
                    :data-testid="'txl-bubble-error-' + (b.turnNo || 0)"
                  >ผิดพลาด</v-chip>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- ── date chip ลอย (แบบไลน์) ──────────────────────────────────────
           บอก **บรรทัดที่กำลังเลื่อนดูอยู่**: ฟอร์มอะไร วันที่เท่าไร เวลาเท่าไร (เวลาไทย)
           🔴 `pointer-events: none` ที่ตัวแถบ และ `auto` เฉพาะที่ชิป — แถบนี้ลอยทับบับเบิลอยู่
           ตลอดเวลา ถ้ามันรับคลิกเอง บับเบิลใต้มันจะกดเลือกไม่ได้เป็นแถบ ๆ
           🔴 ตัวเลขมาจาก `currentBubble` ซึ่งอัปเดตจาก scroll จริง ไม่ใช่จาก "ช่วงแรกที่เห็น" -->
      <div v-if="currentBubble" class="chat-float-head" data-testid="txl-chat-float-head">
        <button
          type="button"
          class="chat-seg-chip chat-float-chip"
          :class="{
            'chat-seg-chip--active': Boolean(currentBubble.formID) && currentBubble.formID === selectedFormId,
            'chat-seg-chip--none': !currentBubble.formID,
          }"
          :disabled="!currentBubble.formID"
          :title="segTitle(currentBubble)"
          :data-testid="'txl-chat-float-chip-' + (currentBubble.formID || 0)"
          @click="onSegHead(currentBubble)"
        >
          <v-icon size="13" class="mr-1">{{ currentBubble.formID ? 'mdi-file-document-outline' : 'mdi-chat-outline' }}</v-icon>
          <span class="font-weight-medium">{{ currentBubble.formTitle }}</span>
          <span v-if="currentBubble.formInferred" class="chat-seg-approx">≈</span>
          <span class="chat-seg-dot">·</span>
          <span>{{ segWhen(currentBubble) }}</span>
        </button>
      </div>
    </div>

    <!-- 🔴 This caption sits exactly where the playground's composer sits. It is
         the visible half of the read-only guarantee: an admin who scrolls to the
         bottom looking for the text box is told why there is none. -->
    <div class="txl-chat-foot text-caption text-medium-emphasis mt-2" data-testid="txl-chat-noncomposer">
      <v-icon size="14" class="mr-1">mdi-lock-outline</v-icon>
      โหมดอ่านอย่างเดียว — ไม่มีช่องพิมพ์ข้อความ ดูประวัติได้เท่านั้น
    </div>
  </div>
</template>

<script>
// Chat column of the Tax Log V2 viewer (READ-ONLY twin of `TaxPlaygroundChatPanelV2`).
//
// 🔴 THIS COMPONENT CAN NEVER SEND ANYTHING. It has no composer, no quick-reply
// chips, no reset, no `send`/`quick-reply`/`new-form` emit, and it imports no API
// service at all. Its ONLY outbound signal is `select-turn` (a pure view action:
// "show me this turn in the inspector"). Keep it that way — the moment this panel
// grows an input, the whole Tax Log page stops being read-only.
//
// A dumb component, exactly like its playground twin: the parent assembles
// `bubbles` from `GET api/v1/TaxLogV2/sessions/{id}/turns`, where `userMessage`
// and `reply` arrive already DECRYPTED and verbatim (never CSV-mangled the way
// `LogBAL.GetTableLogMessage` mangles them).
//
// 🔴 Tax cards: the parent reads them from the turn's `taxCards` field and hands them down
// ON the bubble (`cards` / `cardText`) — this component never parses the wire itself, and it
// must not start: `reply` is raw only on production rows (the playground's is stripped
// server-side), which is precisely the asymmetry that field exists to hide.
// Cards render BELOW the bubble, as an ADDITION: the bubble text defaults to the
// marker-free copy, and "ดู marker ดิบ" puts the raw string back. `TaxSummaryCardV2` is the
// ONLY thing imported for this, and deliberately so — it pulls in nothing but the
// dependency-free `utils/taxCardFormat`, whereas `TaxPlaygroundChatPanelV2.vue` (the obvious
// "just reuse it") carries a composer and would end this page's read-only guarantee.

import TaxSummaryCardV2 from './TaxSummaryCardV2.vue';
import { fmtThaiDayLabel, fmtThaiHourMinute, fmtThaiTime } from '../utils/thaiTime.js';
import { buildChatSegments } from '../utils/taxLogChatSegments.js';

/**
 * ระยะผ่อนผันจากขอบบนของจอ ก่อนจะถือว่าบับเบิล "เลื่อนพ้นไปแล้ว"
 * เผื่อ padding ของตัว scroller + เศษ sub-pixel เวลาเลื่อนแบบ smooth (0 ทำให้ chip กระพริบสลับสองแถว)
 */
const CHIP_EDGE_PX = 6;

export default {
  name: 'TaxLogChatPanelV2',
  components: { TaxSummaryCardV2 },
  props: {
    /**
     * `{ key, side: 'user'|'bot'|'system', text, time, turnNo, turnKey, isError }[]`,
     * plus on bot bubbles:
     * `cardText` — `text` with every `|taxcard|` payload removed. Present on EVERY bot bubble
     *   (the parent stopped gating it on "this turn has cards"), so on a card-less turn it is
     *   simply EQUAL to `text` — which is what lets `hasRawWire` decide, per bubble, whether
     *   there is a raw wire worth offering. `null`/absent means "this bubble never had a
     *   stripped copy at all" (user and system bubbles) and `bubbleText` falls back to `text`.
     *   🔴 `''` is a THIRD case, not the same as `null`: the turn's whole message WAS the
     *   marker (a card-only turn), or the playground already stripped it server-side and left
     *   nothing behind. See `bubbleText`.
     * `cards`    — `{ raw, doc }[]`, built by the parent from the turn's own `taxCards` field
     *   (NOT re-split out of `reply` — only production rows carry it raw); `doc` is `null`
     *   when that payload failed to parse (`raw` is still shown, in a red alert — evidence is
     *   never hidden on this page).
     * 🔴 No `cardsDiffer` twin of the playground's chip: the parent reads cards from ONE
     *   source and no longer splits the reply itself, so there is no second opinion to differ.
     *
     * `turnKey` is the wire's stable `"lm:{logMessageId}"` / `"pg:{turnId}"` key —
     * used here for selection/highlighting only, never as a request parameter.
     */
    bubbles: { type: Array, default: () => [] },
    /** Highlights the bubbles of the turn currently open in the inspector. */
    activeTurnKey: { type: String, default: null },
    /**
     * ฟอร์มที่แผง ฟอร์ม / Tax JSON กำลังอ่านอยู่ — ใช้ไฮไลต์หัวคั่นของช่วงนั้น เพื่อให้ตอบได้ทันทีว่า
     * "บทสนทนาช่วงไหนคือฟอร์มที่เปิดดูอยู่" (0 = ยังไม่ได้เลือก/ไม่มี)
     */
    selectedFormId: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
    /** Total turns of the session (may exceed `bubbles` while more pages load). */
    turnCount: { type: Number, default: 0 },
  },
  // 🔴 ยังไม่มี emit ที่เขียนอะไรทั้งสิ้น — ทั้งสองตัวเป็น view action ล้วน ๆ ("แสดงเทิร์นนี้" /
  // "ชี้แผงฟอร์มไปที่ฟอร์มนี้") หน้านี้ยังส่งข้อความไม่ได้เหมือนเดิม
  emits: ['select-turn', 'select-form'],
  data() {
    return {
      /**
       * Local-only view state: flips every bubble back to the raw reply, markers included.
       * 🔴 It does NOT hide the cards — they keep rendering underneath. The switch answers
       * "what did the wire look like", not "show me less".
       */
      rawWire: false,
      /**
       * `key` ของบับเบิลที่อยู่บนสุดของจอตอนนี้ — ต้นทางของทุกตัวอักษรบน date chip ที่ลอยอยู่
       * `null` = ยังไม่เคยวัด (chip จะตกไปใช้บับเบิลแรก ⇒ มีอะไรให้อ่านตั้งแต่ก่อนเลื่อนครั้งแรก)
       */
      currentKey: null,
      /** handle ของ rAF ที่หน่วง `syncCurrentBubble` — 0 = ไม่มีคิวค้าง (ดู `onScroll`) */
      chipRaf: 0,
    };
  },
  computed: {
    /**
     * บับเบิลที่ติดกัน → "ช่วง" ตาม (ฟอร์ม × วันตามเวลาไทย) — 1 ช่วง = 1 หัวคั่นลอย
     *
     * 🔴 ตรรกะการยุบอยู่ใน `utils/taxLogChatSegments.js` (pure + มีเทสต์ของตัวเอง) ที่นี่แค่เรียกใช้:
     * ฟิลด์ `formID` / `formTitle` / `formInferred` ถูก parent แนบมากับ **ทุกบับเบิล** แล้ว
     * (`TaxLogV2Detail.bubbles()`) panel ไม่เคยหาฟอร์มเอง — มันไม่มี `forms[]` ให้หาอยู่แล้ว
     */
    segments() {
      return buildChatSegments(this.bubbles);
    },
    /**
     * บับเบิลที่ date chip กำลังพูดถึง = **บรรทัดบนสุดที่ยังอยู่ในจอ** (อัปเดตจาก scroll จริง)
     *
     * 🔴 ตกกลับไปที่บับเบิลแรกเมื่อยังไม่เคยวัด หรือคีย์เดิมหลุดไปแล้ว (โหลดเทิร์นเพิ่ม / เปลี่ยน
     * session) — chip ที่หายไปเงียบ ๆ ตอนเลื่อนอ่านว่า "พัง" มากกว่า chip ที่ยังตอบว่าเริ่มที่ไหน
     */
    currentBubble() {
      const list = this.bubbles || [];
      if (!list.length) return null;
      return list.find((b) => b && b.key === this.currentKey) || list[0];
    },
    /**
     * Is there a raw wire ANY of these bubbles could show that its default text does not?
     * i.e. does some bot bubble's `text` (what production actually sent) differ from `cardText`
     * (the marker-free copy the bubble prints by default)?
     *
     * 🔴 This is the switch's gate, and it is NOT "does some turn have a card". That was the old
     * `hasCards` test and it became a LIE the moment cards started arriving in their own `taxCards`
     * field: a playground turn carries cards while its `reply` was already stripped server-side, so
     * `text === cardText` and the switch flipped between two identical strings — a control that
     * visibly does nothing is worse than no control.
     * 🔴 Still "carried, not parsed": a payload whose JSON is malformed STILL makes the two strings
     * differ (the marker block is cut out of `cardText` whether or not it parses), so the turn that
     * most needs the raw wire keeps its switch — which is exactly what the old comment defended.
     * 🔴 Compared through `String(x ?? '')` because `text` can be `null`/absent while `cardText` is
     * `''` (a card-only turn). Those two print identically, so they must not count as a difference.
     */
    hasRawWire() {
      return (this.bubbles || []).some(
        (b) => b && b.cardText != null && String(b.text ?? '') !== String(b.cardText),
      );
    },
  },
  watch: {
    /**
     * 🔴 The timeline is NEWEST-first while `.chat-scroll` starts at the OLDEST
     * bubble, so on a long session the two `chat-bubble--active` outlines land
     * hundreds of bubbles below the fold and clicking a timeline card reads as a
     * no-op. Bring the selected turn into view whenever the selection changes —
     * including when the parent selects it from a deep link on mount.
     */
    activeTurnKey: {
      immediate: true,
      handler() {
        this.scrollActiveIntoView();
      },
    },
    /** The bubbles arrive AFTER the deep link picks a turn — re-run once they exist. */
    bubbles() {
      this.scrollActiveIntoView();
      // แถวเปลี่ยนไปทั้งชุด (โหลดครั้งแรก / กด "โหลดเทิร์นเพิ่ม") ⇒ ตำแหน่งเดิมใช้ไม่ได้แล้ว ต้องวัดใหม่
      this.$nextTick(() => this.syncCurrentBubble());
    },
  },
  mounted() {
    // วัดครั้งแรกโดยไม่ต้องรอให้ใครเลื่อน — chip ต้องมีคำตอบตั้งแต่จอแรก
    this.$nextTick(() => this.syncCurrentBubble());
  },
  beforeUnmount() {
    if (this.chipRaf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(this.chipRaf);
    this.chipRaf = 0;
  },
  methods: {
    /**
     * ทุก scroll event → วัดใหม่ 1 ครั้งต่อเฟรม
     * 🔴 หน่วงด้วย rAF ไม่ใช่ debounce แบบมี timeout: chip ต้องขยับ **ระหว่าง**เลื่อน ไม่ใช่หลังหยุดเลื่อน
     * (debounce จะทำให้ chip ค้างบอกเวลาเก่าตลอดการลากยาว ๆ ซึ่งเป็นช่วงที่คนต้องการมันที่สุด)
     */
    onScroll() {
      if (this.chipRaf) return;
      if (typeof requestAnimationFrame !== 'function') {
        this.syncCurrentBubble();
        return;
      }
      this.chipRaf = requestAnimationFrame(() => {
        this.chipRaf = 0;
        this.syncCurrentBubble();
      });
    },
    /**
     * หาบับเบิลแรกที่ขอบล่างยังอยู่ต่ำกว่าขอบบนของจอ = "บรรทัดที่กำลังอ่าน"
     *
     * 🔴 ค้นแบบ binary ไม่ใช่ไล่ทีละตัว: แถวเรียงบนลงล่างเสมอ เงื่อนไข `bottom > ขอบบน` จึง monotonic
     * ⇒ อ่าน `getBoundingClientRect` แค่ ~log₂(n) ครั้งต่อเฟรม แทนที่จะเป็นหลักร้อย (session จริงมี
     * ได้เป็นพันบับเบิล และโค้ดนี้ทำงานทุกเฟรมที่นิ้วยังลากอยู่)
     * 🔴 อ่าน rect อย่างเดียว ไม่เขียน DOM ระหว่างลูป — ไม่งั้นจะเกิด layout thrash ทีละรอบ
     */
    syncCurrentBubble() {
      const sc = this.$refs.scroller;
      if (!sc || !sc.querySelectorAll) return;
      const nodes = sc.querySelectorAll('[data-bubble-key]');
      if (!nodes.length) {
        this.currentKey = null;
        return;
      }
      const limit = sc.getBoundingClientRect().top + CHIP_EDGE_PX;
      let lo = 0;
      let hi = nodes.length - 1;
      let found = nodes.length - 1;   // เลื่อนสุดล่างแล้วไม่เจอใครเลย ⇒ บรรทัดสุดท้ายคือสิ่งที่เห็นอยู่
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (nodes[mid].getBoundingClientRect().bottom > limit) {
          found = mid;
          hi = mid - 1;
        } else {
          lo = mid + 1;
        }
      }
      const key = nodes[found].getAttribute('data-bubble-key');
      if (key !== this.currentKey) this.currentKey = key;
    },
    scrollActiveIntoView() {
      if (!this.activeTurnKey) return;
      this.$nextTick(() => {
        // 🔴 `.txl-cards--active` is in the selector because a CARD-ONLY turn renders no
        // bubble at all (see the template guard) — without it, selecting such a turn from
        // the timeline would find nothing and silently not scroll.
        const el = this.$el && this.$el.querySelector
          && this.$el.querySelector('.chat-bubble--active, .txl-cards--active');
        if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      });
    },
    /**
     * 🔴 Falls back to `b.text` only when `cardText` is ABSENT (`null`/undefined) — user and
     * system bubbles never had a stripped copy and must still show their own text. (A card-LESS
     * bot turn now carries a `cardText` equal to `text`, so it prints the same either way.)
     * `''` is NOT absent: it is a card-only turn whose whole message WAS the marker, and falling
     * back there would put the raw `|taxcard|(…)` payload on screen with the switch off.
     * 🔴 …UNLESS that turn has no card either. The stripped copy is empty AND nothing rendered in
     * its place means the marker's payload was unusable — a production turn whose card JSON is
     * malformed cuts the marker out of `cardText` but yields NO card (the wire's `taxCards` is one
     * array string: one bad document takes the whole array down). Printing the empty copy there
     * leaves a blank grey bubble where the evidence used to be. Showing the raw string is what this
     * page did before cards were read from their own field, and this branch keeps it: debris on
     * screen beats a bubble that says nothing. It cannot leak a marker on a turn that HAS a card —
     * it requires `cards` to be empty.
     * 🔴 Always a string — the template calls `.trim()` on the result to decide whether to render.
     */
    bubbleText(b) {
      if (!b) return '';
      const raw = b.text == null ? '' : String(b.text);
      if (this.rawWire || b.cardText == null) return raw;
      const clean = String(b.cardText);
      if (!clean.trim() && !(b.cards && b.cards.length)) return raw;
      return clean;
    },
    /**
     * Is a bubble rendered for this row at all?
     *
     * 🔴 Lives in ONE place because two things depend on the same answer: the bubble's own `v-if`,
     * and `txl-cards--active` (the card block only stands in for the bubble's selection outline
     * when there is no bubble to outline). Inlining the expression twice is how they drift apart.
     * Drops the bubble only when there is nothing to say AND a card is there to say it instead —
     * see the template comment above the bubble for the full reasoning.
     */
    showBubble(b) {
      return Boolean(this.bubbleText(b).trim() || !(b && b.cards && b.cards.length));
    },
    /**
     * `2 ก.ย. 2026 14:32 น.` — เวลาไทยเสมอ (ค่าบน wire เป็น UTC **ไม่มี `Z`** — ดู `utils/thaiTime.js`)
     * ใช้ร่วมกันทั้งหัวคั่นในสาย (`seg`) และ date chip ที่ลอยอยู่ (`currentBubble`) เพราะทั้งสองก้อนมี
     * รูปเดียวกัน `{ formID, formTitle, formInferred, time }` — ป้ายสองที่ต้องพูดเหมือนกันเสมอ
     */
    segWhen(seg) {
      if (!seg || !seg.time) return 'ไม่ทราบเวลา';
      return `${fmtThaiDayLabel(seg.time, '—')} ${fmtThaiHourMinute(seg.time)} น.`;
    },
    /**
     * tooltip ของหัวคั่น — **พูดความจริงเรื่องที่มาของป้าย** ไม่ใช่แค่บอกว่าคลิกได้
     * ช่วงของแถว production เป็นค่าที่อนุมานจากเวลา (ดู `utils/taxLogChatSegments.js`) คนอ่าน log
     * ต้องรู้ว่าอันไหนคือค่าที่บันทึกไว้จริง อันไหนคือค่าที่หน้าจอเดาให้
     */
    segTitle(seg) {
      if (!seg || !seg.formID) {
        return 'บทสนทนาก่อนที่ session นี้จะมีฟอร์มภาษีใบแรก';
      }
      const click = `คลิกเพื่อให้แผง ฟอร์ม / Tax JSON อ่านฟอร์ม #${seg.formID}`;
      return seg.formInferred
        ? `แชทจริงไม่ได้บันทึก FormID ไว้รายเทิร์น — ช่วงนี้แบ่งจากเวลาที่ฟอร์มถูกสร้าง (ค่าประมาณ) · ${click}`
        : `เทิร์นในช่วงนี้บันทึก FormID ไว้ตรง ๆ · ${click}`;
    },
    /** ชิป (ทั้งในสายและตัวที่ลอย) ทำได้อย่างเดียว: ชี้แผงฟอร์ม/Tax JSON ไปที่ฟอร์มนั้น — ยังอ่านอย่างเดียว */
    onSegHead(seg) {
      if (seg && seg.formID) this.$emit('select-form', seg.formID);
    },
    /** The one thing a bubble does: point the inspector at its turn. */
    onBubble(b) {
      if (b && b.turnKey) this.$emit('select-turn', b.turnKey);
    },
    bubbleClass(b) {
      const base = b.side === 'system'
        ? 'chat-bubble--system'
        : (b.side === 'user' ? 'chat-bubble--user' : 'chat-bubble--bot');
      const active = b.turnKey && b.turnKey === this.activeTurnKey ? ' chat-bubble--active' : '';
      return base + active;
    },
    bubbleTitle(b) {
      if (!b || !b.turnKey) return null;
      return `เทิร์น #${b.turnNo} — คลิกเพื่อดูรายละเอียดในคอลัมน์ขวา`;
    },
    /** A7 — the speaker is announced in text, not only by which side the bubble sits on. */
    srLabel(b) {
      if (b.side === 'user') return 'ผู้ใช้: ';
      if (b.side === 'system') return 'ระบบ: ';
      return 'บอท: ';
    },
    /** เวลาใต้บับเบิล = **เวลาไทย** (ค่ามาจาก `createdDate` ซึ่งเป็น UTC ไม่มี `Z` — ดู `utils/thaiTime.js`) */
    fmtTime(value) {
      return fmtThaiTime(value);
    },
  },
};
</script>

<style scoped>
/* Copied from `TaxPlaygroundChatPanelV2.vue` so the two transcripts read as the
   same product. The composer rules are dropped (there is no composer) and
   `.chat-bubble--active` is added for inspector selection. The card rules are the
   playground's `.tpg-*` ones renamed to `.txl-*` — copied, not imported, because
   `<style scoped>` is per-file and importing the playground panel is forbidden here. */
/* 🔴 `height: 100%` is what makes `.chat-scroll` the ONLY scroller in this column.
   Without it the flex column is as tall as its content, `flex: 1 1 auto` on the
   bubble list has nothing to grow into, and the whole panel overflows its column
   so the transcript drags the column's own scrollbar around instead. Where this
   panel sits in an auto-height tab body (xs), 100% resolves to `auto` and the
   behaviour is unchanged. Same rule as `TaxPlaygroundChatPanelV2.vue`. */
.txl-chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

/* Head / raw-wire switch state their own height — only the bubble list is
   elastic, and it stops shrinking at its `min-height`. */
.txl-chat > :not(.chat-view) {
  flex: 0 0 auto;
}

/* จอของแชท: ตัวเลื่อนอยู่ข้างใน ส่วน date chip ลอยทับด้วย `position: absolute` ของชั้นนี้
   🔴 `position: relative` + `min-height: 0` เป็นของจำเป็น ไม่ใช่ของตกแต่ง — ไม่มี relative ชิปจะไป
   อ้างอิงกับ `.v-application__wrap` แล้วลอยไปอยู่กลางหน้าจอ, ไม่มี min-height: 0 ลูก flex จะไม่ยอมหด
   ⇒ ตัวเลื่อนดันคอลัมน์จนทั้งหน้าเลื่อนแทน (เหตุผลเดียวกับที่ `.chat-scroll` เคยต้องมี) */
.chat-view {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.txl-chat-head {
  position: sticky;
  top: 0;
  z-index: 1;
  background: rgb(var(--v-theme-surface));
}

.chat-scroll {
  flex: 1 1 auto;
  min-height: 120px;
  overflow-y: auto;
  /* 🔴 ที่ว่างด้านบน 30px = ที่ยืนของ date chip ที่ลอยอยู่ ถ้าไม่เว้น บับเบิลแรกจะโดนชิปทับตั้งแต่ยัง
     ไม่เลื่อน (และหัวคั่นของช่วงแรกจะซ้อนกับชิปจนอ่านเป็นข้อความซ้ำสองอัน) */
  padding: 30px 2px 4px;
}

.chat-col {
  max-width: 82%;
}

/* 🔴 A card carries a table-like breakdown; the 82% chat width squeezes the amount
   column until the numbers wrap, so a bubble that has cards takes the full row. */
.chat-col--card {
  max-width: 100%;
  width: 100%;
}

.txl-cards {
  width: 100%;
}

/* The card block stands in for the bubble's outline on a CARD-ONLY turn (no bubble
   is rendered there), so the transcript and the inspector never disagree about
   which turn is open. STAND-IN is literal: the template gates the class on
   `!showBubble(b)`, so a selected turn is outlined by its bubbles OR by this block,
   never by both — one selection must not draw three nested outlines. Rounded a
   little wider than the card itself so the outline does not sit on top of the
   card's own border. */
.txl-cards--active {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
  border-radius: 6px;
}

.txl-card-raw {
  margin: 4px 0 0;
  max-height: 180px;
  overflow: auto;
  font-size: 11px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-all;
}

.txl-raw-switch {
  flex: 0 0 auto;
}

/* ── หัวคั่นช่วงของฟอร์ม (อยู่ในสาย ไม่ลอย) ────────────────────────────────────
   ทำหน้าที่เดียว: บอกว่า "ตรงนี้คือรอยต่อ" — ฟอร์มเปลี่ยน หรือ ข้ามวัน
   ส่วน "ตอนนี้กำลังอ่านของฟอร์มไหน เวลาอะไร" เป็นของ `.chat-float-head` ที่ลอยอยู่ข้างบน */
.chat-seg-head {
  display: flex;
  justify-content: center;
  padding: 6px 0 8px;
}

/* ── date chip ลอย (แบบไลน์) ─────────────────────────────────────────────────
   🔴 อยู่ **นอก** `.chat-scroll` (ลูกของ `.chat-view`) จึงไม่เลื่อนไปกับเนื้อหา และ
   🔴 `pointer-events: none` ที่แถบ + `auto` ที่ชิป: แถบนี้กว้างเต็มคอลัมน์และลอยทับบับเบิลตลอดเวลา
   ถ้ารับคลิกเองมันจะกลายเป็นแผ่นใสที่กินคลิกของบับเบิลใต้มัน (กดเลือกเทิร์นไม่ได้เป็นแถบ ๆ) */
.chat-float-head {
  position: absolute;
  top: 4px;
  left: 0;
  right: 0;
  z-index: 3;
  display: flex;
  justify-content: center;
  padding: 0 8px;
  pointer-events: none;
}

/* ลอยทับบทสนทนา ⇒ ต้องอ่านออกบนพื้นทุกแบบ: ทึบกว่าหัวคั่นในสาย + เงาชัดกว่าเล็กน้อย */
.chat-float-chip {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.22);
}

.chat-seg-chip {
  pointer-events: auto;
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 2px 10px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 999px;
  background: #eceff1;
  color: #455a64;
  font-size: 11px;
  line-height: 1.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
}

.chat-seg-chip:hover:not(:disabled) {
  background: #dde3e6;
}

/* ฟอร์มที่แผงด้านขวากำลังอ่านอยู่ — ตอบ "ช่วงไหนคือฟอร์มที่เปิดดูอยู่" โดยไม่ต้องเทียบเลขเอง */
.chat-seg-chip--active {
  background: rgb(var(--v-theme-primary));
  border-color: transparent;
  color: #fff;
}

/* ช่วงที่ยังไม่มีฟอร์ม (ทักทาย/คุยเรื่องอื่นก่อนเข้าฟอร์ม) — ไม่มีอะไรให้คลิก */
.chat-seg-chip--none,
.chat-seg-chip:disabled {
  cursor: default;
  opacity: 0.85;
}

/* `≈` = ป้ายฟอร์มของช่วงนี้อนุมานจากเวลา ไม่ใช่ค่าที่บันทึกไว้ (tooltip อธิบายเต็ม) */
.chat-seg-approx {
  margin-left: 3px;
  opacity: 0.75;
}

.chat-seg-dot {
  margin: 0 5px;
  opacity: 0.55;
}

/* 🔴 `position: relative` is LOAD-BEARING, not styling — do not drop it.
   It makes the bubble the containing block for its `.sr-only` label. That label is
   `position: absolute`, and an absolutely positioned box is clipped by an ancestor's
   `overflow` ONLY if that ancestor is between it and its containing block. Without a
   positioned bubble the nearest one is `.v-application__wrap`, so the label escapes
   `.chat-scroll`, `.tpg-col`, `.tpg-page` AND `.v-main` alike: its static position
   sits wherever its bubble scrolled to, so a long transcript stretches the WHOLE app
   and gives the page a scrollbar that grows with the chat and ignores the window
   size. (Measured on the playground twin: app overflow 406 px → 0 with this line.) */
.chat-bubble {
  position: relative;
  max-width: 100%;
  padding: 8px 12px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  cursor: pointer;
  transition: filter 0.12s ease;
}

.chat-bubble:hover {
  filter: brightness(0.96);
}

.chat-bubble--bot {
  background: #eceff1;
  color: #1a1a1a;
  border-top-left-radius: 4px;
}

.chat-bubble--user {
  background: #1e88e5;
  color: #fff;
  border-top-right-radius: 4px;
}

.chat-bubble--system {
  background: #ffebee;
  color: #b71c1c;
  border-top-left-radius: 4px;
  cursor: default;
}

/* The selected turn keeps BOTH its bubbles outlined, so the transcript and the
   inspector never disagree about which turn is open. */
.chat-bubble--active {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 1px;
}

.chat-time {
  font-size: 11px;
  padding: 0 4px;
  opacity: 0.75;
}

.txl-chat-foot {
  position: sticky;
  bottom: 0;
  background: rgb(var(--v-theme-surface));
  padding-top: 4px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
